import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Star, Gift, Share2, Copy, CheckCircle2, Loader2, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import ImmersiveLayout from "../components/ImmersiveLayout";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const TYPE_LABELS = {
  order_completed: { id: "Pesanan selesai", en: "Order completed" },
  email_verified: { id: "Email terverifikasi", en: "Email verified" },
  referral_bonus: { id: "Bonus referral", en: "Referral bonus" },
  redeemed: { id: "Ditukar", en: "Redeemed" },
};

export default function LoyaltyPage() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const id = language === "id";

  const [data, setData] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      axios.get(`${API}/user/loyalty`, { withCredentials: true }),
      axios.get(`${API}/user/referral-stats`, { withCredentials: true })
    ]).then(([loyRes, refRes]) => {
      setData(loyRes.data);
      setReferralStats(refRes.data);
    }).catch(() => { }).finally(() => setLoading(false));
  }, [user]);

  const copyCode = () => {
    const code = data?.referral_code || "";
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success(id ? "Kode disalin!" : "Code copied!");
    });
  };

  if (!user) {
    return (
      <ImmersiveLayout>
        <section className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <Star size={40} className="mx-auto text-seikat-pink mb-4" />
            <p className="font-heading text-3xl text-seikat-bone mb-4">Sign in to view your rewards</p>
            <Link to="/login" className="btn-pill btn-pill--solid">Sign In</Link>
          </div>
        </section>
      </ImmersiveLayout>
    );
  }

  return (
    <ImmersiveLayout>
      <section className="relative pt-32 pb-20 px-6 lg:px-16 min-h-screen">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }} className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-px bg-seikat-lime" />
              <span className="eyebrow-immersive">{id ? "Hadiah" : "Rewards"}</span>
            </div>
            <h1 className="font-heading text-5xl lg:text-6xl text-seikat-bone leading-[0.95]">
              {id ? <>Poin <em>Loyalitas</em>.</> : <>Loyalty <em>Rewards</em>.</>}
            </h1>
          </motion.div>

          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="animate-spin text-seikat-pink" size={32} /></div>
          ) : data && (
            <div className="space-y-8">
              {/* Points card */}
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                className="glass-dark rounded-3xl p-8 border border-seikat-pink/20"
                data-testid="loyalty-points-card"
              >
                <div className="flex flex-col md:flex-row gap-8 items-start md:items-center justify-between">
                  <div>
                    <p className="eyebrow-immersive mb-3">{id ? "Total Poin Kamu" : "Your Total Points"}</p>
                    <div className="flex items-end gap-3">
                      <span className="font-heading text-8xl text-seikat-bone" data-testid="points-value">{data.points?.toLocaleString()}</span>
                      <span className="font-heading text-2xl text-seikat-pink mb-3">{id ? "poin" : "pts"}</span>
                    </div>
                    {!data.is_email_verified && (
                      <Link to="/verify-email" className="flex items-center gap-2 mt-4 text-[#C5DB7B] font-body text-sm hover:text-white transition-colors">
                        <CheckCircle2 size={14} /> {id ? "Verifikasi email & dapatkan +50 poin" : "Verify email & earn +50 pts"}
                      </Link>
                    )}
                  </div>
                  <div className="flex flex-col gap-3">
                    <div className="glass-dark rounded-2xl px-6 py-4 border border-white/10 text-center">
                      <p className="eyebrow-immersive mb-2">{id ? "Per pesanan selesai" : "Per completed order"}</p>
                      <p className="font-body text-seikat-bone text-sm">1 {id ? "poin" : "pt"} / Rp 10,000</p>
                    </div>
                    <div className="glass-dark rounded-2xl px-6 py-4 border border-white/10 text-center">
                      <p className="eyebrow-immersive mb-2">{id ? "Verifikasi email" : "Email verification"}</p>
                      <p className="font-body text-seikat-bone text-sm">+50 {id ? "poin" : "pts"}</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Referral card */}
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                className="glass-dark rounded-3xl p-8 border border-seikat-lime/20"
                data-testid="referral-card"
              >
                <div className="flex items-center gap-3 mb-5">
                  <Gift size={20} className="text-seikat-lime" />
                  <h2 className="font-heading text-2xl text-seikat-bone">
                    {id ? "Program Referral" : "Referral Program"}
                  </h2>
                </div>
                <p className="font-body text-seikat-bone/60 mb-6 text-sm leading-relaxed">
                  {id
                    ? "Ajak teman kamu bergabung ke Seikat dan dapatkan 100 poin untuk setiap teman yang mendaftar menggunakan kode referralmu."
                    : "Invite your friends to Seikat and earn 100 points for each friend who signs up using your referral code."}
                </p>
                <div className="flex items-center gap-3">
                  <div className="flex-1 flex items-center gap-3 glass-dark rounded-xl px-5 py-3 border border-white/10">
                    <span className="font-mono text-2xl font-bold text-seikat-lime tracking-widest" data-testid="referral-code">
                      {data.referral_code || "—"}
                    </span>
                  </div>
                  <button onClick={copyCode} className="btn-pill btn-pill--solid">
                    {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                    {copied ? (id ? "Disalin!" : "Copied!") : (id ? "Salin" : "Copy")}
                  </button>
                </div>
                {referralStats && (
                  <p className="mt-4 font-body text-seikat-bone/50 text-xs">
                    {referralStats.referrals_count} {id ? "teman sudah mendaftar" : "friends signed up"} · +{referralStats.referrals_count * 100} {id ? "poin earned" : "pts earned"}
                  </p>
                )}
              </motion.div>

              {/* Points history */}
              {data.ledger?.length > 0 && (
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                  className="glass-dark rounded-3xl p-8 border border-white/10"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <TrendingUp size={18} className="text-seikat-pink" />
                    <h2 className="font-heading text-2xl text-seikat-bone">
                      {id ? "Riwayat Poin" : "Points History"}
                    </h2>
                  </div>
                  <div className="space-y-3">
                    {data.ledger.map((entry, i) => (
                      <div key={entry.ledger_id || i} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                        <div>
                          <p className="font-body text-seikat-bone text-sm">
                            {(TYPE_LABELS[entry.type] || {})[language] || entry.type}
                          </p>
                          <p className="font-body text-seikat-bone-muted text-xs">{entry.created_at?.slice(0, 10)}</p>
                        </div>
                        <span className={`font-heading text-xl ${entry.type === "redeemed" ? "text-red-400" : "text-[#C5DB7B]"}`}>
                          {entry.type === "redeemed" ? "-" : "+"}{entry.points}
                        </span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          )}
        </div>
      </section>
    </ImmersiveLayout>
  );
}
